import React, { useEffect, useState, useCallback } from "react";
import { CheckCircle, XCircle, AlertCircle, Info, X } from "lucide-react";
import { _registerToastListener, _registerConfirmListener } from "../../utils/notify";

const TOAST_CONFIG = {
  success: { bg: "bg-green-50 border-green-200", text: "text-green-800", icon: CheckCircle, iconColor: "text-green-500" },
  error: { bg: "bg-red-50 border-red-200", text: "text-red-800", icon: XCircle, iconColor: "text-red-500" },
  warning: { bg: "bg-yellow-50 border-yellow-200", text: "text-yellow-800", icon: AlertCircle, iconColor: "text-yellow-500" },
  info: { bg: "bg-blue-50 border-blue-200", text: "text-blue-800", icon: Info, iconColor: "text-blue-500" },
};

const ToastItem = ({ toast, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(toast.id), 4000);
    return () => clearTimeout(timer);
  }, [toast.id, onClose]);

  const config = TOAST_CONFIG[toast.type] || TOAST_CONFIG.info;
  const Icon = config.icon;

  return (
    <div
      role="alert"
      className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg min-w-[280px] max-w-sm ${config.bg} animate-in slide-in-from-bottom-5 fade-in duration-300`}
    >
      <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${config.iconColor}`} />
      <p className={`text-sm font-medium flex-1 ${config.text}`}>{toast.message}</p>
      <button
        onClick={() => onClose(toast.id)}
        className="flex-shrink-0 p-1 rounded-md hover:bg-black/5 transition-colors"
        aria-label="Close notification"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const ConfirmModal = ({ confirmState, onResolve }) => {
  if (!confirmState) return null;
  const { title, message, confirmText = "Confirm", cancelText = "Cancel", danger = false } = confirmState;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 p-4" style={{ zIndex: 2000000 }}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 animate-in fade-in zoom-in-95 duration-150">
        {title && <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>}
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => onResolve(false)}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={() => onResolve(true)}
            className={`px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors ${
              danger ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Mount once near the app root. Provides the single rendering surface for
 * notify.success/error/warning/info() and confirmDialog() calls from anywhere
 * in the app, replacing native alert()/confirm() with a consistent, styled UI.
 */
const NotificationCenter = () => {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);

  const closeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    _registerToastListener((toast) => setToasts((prev) => [...prev, toast]));
    _registerConfirmListener((state) => setConfirmState(state));
  }, []);

  const resolveConfirm = (result) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };

  return (
    <>
      <div className="fixed bottom-6 right-6 flex flex-col gap-3 items-end" style={{ zIndex: 2000000 }}>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onClose={closeToast} />
        ))}
      </div>
      <ConfirmModal confirmState={confirmState} onResolve={resolveConfirm} />
    </>
  );
};

export default NotificationCenter;
